
import React from "react";
import { Button } from "@/components/ui/button";
import FeaturedStartupCard from "@/components/startup/FeaturedStartupCard";
import { motion } from "framer-motion";

const Startups = () => {
  // Empty featured startups data - ready for real data
  const featuredStartups: any[] = [];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div 
      className="min-h-screen bg-background"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Hero Section */}
      <motion.div 
        className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent py-16"
        variants={sectionVariants}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Featured <span className="text-primary">Startups</span>
            </motion.h1>
            <motion.p 
              className="text-xl text-muted-foreground mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Discover inspiring stories of young entrepreneurs who turned their ideas into successful startups. 
              Learn from their journeys, challenges, and victories.
            </motion.p>
          </div>
        </div>
      </motion.div>

      {/* Featured Startups Section */}
      <motion.div 
        className="container mx-auto px-4 py-16"
        variants={sectionVariants}
      >
        {featuredStartups.length > 0 ? (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
          >
            {featuredStartups.map((startup, index) => (
              <motion.div
                key={startup.name}
                variants={{
                  hidden: { opacity: 0, y: 50 },
                  visible: { 
                    opacity: 1, 
                    y: 0,
                    transition: {
                      duration: 0.5,
                      delay: index * 0.1
                    }
                  }
                }}
              >
                <FeaturedStartupCard {...startup} />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-2xl font-semibold mb-4">No Featured Startups Yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Featured startups will appear here once they are added to the platform.
              </p>
            </motion.div>
          </div>
        )}
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="bg-muted/50 py-16"
        variants={sectionVariants}
      >
        <div className="container mx-auto px-4 text-center">
          <motion.h2 
            className="text-3xl font-bold mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Ready to Start Your Journey?
          </motion.h2>
          <motion.p 
            className="text-muted-foreground mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Join our community of young entrepreneurs and turn your ideas into reality. 
            Get access to mentors, resources, and funding opportunities.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button size="lg" className="bg-gradient-to-r from-primary to-primary/80">
              Join the Community
            </Button>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Startups;
